import React, { useState, useMemo, useEffect } from 'react';
import { TextInput, required, useDataProvider } from 'react-admin';
import { makeStyles, Grid } from '@material-ui/core';
import { useFormState } from 'react-final-form';
import { get, uniq } from 'lodash';

import CheckBoxFilter from './filters/CheckBoxFilter';
import DateRangeFilter from '../../core/components/DateRangeFilter';
import SelectFilter from './filters/SelectFilter';
import useNotifyServerError from '../../core/hooks/useNotifyServerError';

const limitToCall = 50;

const SelectFilterWithDependency = props => {
  const {
    dependsOn,
    dependsOnFilterValues,
    lov,
    dependsOnFilterMultiple,
    source
  } = props;
  const dataProvider = useDataProvider();
  const notifyServerError = useNotifyServerError();
  const [data, setData] = useState();
  const formState = useFormState(props);

  /*
    Check on every change on the other filter,
    And if needed fetch the new values for the current filter
  */
  useEffect(() => {
    /*
      If the current filter is dependent on another filter,
      call an API on the association table to get list of linked values
    */
    if (
      dependsOnFilterValues &&
      (!dependsOnFilterMultiple ||
        (dependsOnFilterValues.length > 0 &&
          dependsOnFilterValues.length <= limitToCall))
    ) {
      let valuesToSearch = dependsOnFilterValues;
      let valuesToSearchIsArray = false;
      if (Array.isArray(dependsOnFilterValues)) {
        valuesToSearch = `[${dependsOnFilterValues}]`;
        valuesToSearchIsArray = true;
      }
      let resource = null;
      let target;
      if (source.includes('fundId') && dependsOn.includes('fundFamiliesId')) {
        resource = 'funds';
        target = 'fundFamilyId';
      } else if (source.includes('investorId')) {
        if (dependsOn.includes('fundId')) {
          resource = 'fundinvestors';
          target = 'fundId';
        }
      } else if (source.includes('fundId')) {
        if (dependsOn.includes('investorId')) {
          resource = 'fundinvestors';
          target = 'investorId';
        }
      } else if (source.includes('userId')) {
        if (dependsOn.includes('investorId')) {
          resource = 'investorcontacts';
          target = 'investorId';
        } else if (dependsOn.includes('fundId') && !valuesToSearchIsArray) {
          resource = 'userauthorizations';
          target = 'fundId';
        }
      }

      if (resource) {
        dataProvider
          .getManyReference(resource, {
            id: valuesToSearch,
            target
          })
          .then(({ data }) => {
            setData(data);
          })
          .catch(notifyServerError);
      }
    }
  }, [
    dataProvider,
    dependsOn,
    dependsOnFilterValues,
    notifyServerError,
    dependsOnFilterMultiple,
    source
  ]);

  /**
   * Filter list of values (LOV) with the result of the query
   */
  const lovOverride = useMemo(() => {
    /*
      If the current filter is dependent on another filter,
      filter with the data received from the API 
    */
    if (
      data &&
      Array.isArray(data) &&
      dependsOnFilterValues &&
      (!dependsOnFilterMultiple ||
        (dependsOnFilterValues.length > 0 &&
          dependsOnFilterValues.length <= limitToCall))
    ) {
      if (data.length === 0) {
        //NOSONAR cwe
        return [];
      }
      let filterSource = null;
      // Users filter which depends on investors filter or funds filter
      if (
        (dependsOn.includes('investorIds') || dependsOn.includes('fundId')) &&
        source.includes('userIds')
      ) {
        filterSource = 'userId';
      }
      if (dependsOn.includes('fundFamiliesId') && source.includes('fundId')) {
        filterSource = 'id';
      }
      // Investors filter which depends on users filter or funds filter
      if (dependsOn.includes('fundId') && source.includes('investorId')) {
        filterSource = 'investorId';
      }

      // Funds filter which depends on investors filter
      if (dependsOn.includes('investorId') && source.includes('fundId')) {
        filterSource = 'fundId';
      }

      // Filter values in the current filter with the results of the request
      if (filterSource) {
        const listIds = uniq(data.map(item => item[filterSource]));
        const lovFiltered = lov.filter(item => listIds.includes(item.value));
        const { filters } = formState.values;

        if (filters && filters.investorIds) {
          const { investorIds } = filters;
          const allValuesCheckedFilters =
            formState.values.allValuesCheckedFilters;

          if (
            allValuesCheckedFilters &&
            lovFiltered.length > investorIds.length
          ) {
            allValuesCheckedFilters.investorIds = false;
          }
        }
        if (filters && filters.fundIds) {
          const { fundIds } = filters;
          const allValuesCheckedFilters =
            formState.values.allValuesCheckedFilters;

          if (allValuesCheckedFilters && lovFiltered.length > fundIds.length) {
            allValuesCheckedFilters.fundIds = false;
          }
        }
        return lovFiltered;
      }
    }

    return lov;
  }, [
    lov,
    data,
    dependsOn,
    dependsOnFilterValues,
    dependsOnFilterMultiple,
    source,
    formState
  ]);

  return React.cloneElement(props.children, {
    ...props,
    lov: lovOverride
  });
};

const useFilterStyles = makeStyles({
  checkBoxInput: {
    display: 'flex',
    marginTop: '17px'
  },
  containerDateRange: {
    alignItems: 'baseline'
  }
});

/**
 * Filter component
 * Displays different filter depends on the type
 * and uses the input's behavior to be used in a form
 * @param {*} props
 */
const Filter = props => {
  const {
    type,
    filtersAreDependants,
    source,
    crossFilters,
    filters,
    sort
  } = props;
  const classes = useFilterStyles(props);
  const formState = useFormState();

  if (filtersAreDependants && type.includes('Select')) {
    const indexSource = crossFilters.findIndex(filter =>
      source.includes(filter)
    );
    let dependsOn = indexSource >= 1 && crossFilters[indexSource - 1];

    if (dependsOn) {
      const dependsOnFilter = filters.find(
        filter => filter.parameter === dependsOn
      );
      const dependsOnFilterMultiple =
        dependsOnFilter && dependsOnFilter.type === 'MultiSelect';
      const dependsOnFilterValues = get(
        formState,
        `values.filters.${dependsOn}`,
        dependsOnFilterMultiple ? [] : null
      );

      return (
        <Grid item xs={2}>
          <SelectFilterWithDependency
            {...props}
            dependsOn={dependsOn}
            dependsOnFilterValues={dependsOnFilterValues}
            dependsOnFilterMultiple={dependsOnFilterMultiple}
          >
            <SelectFilter
              validate={props.required && [required()]}
              multiple={type === 'MultiSelect'}
              sort={sort}
            />
          </SelectFilterWithDependency>
        </Grid>
      );
    }
  }

  // Multi select input which not displays more 2 elements in the field
  if (type === 'MultiSelect') {
    return (
      <Grid item xs={2}>
        <SelectFilter {...props} validate={props.required && [required()]} />
      </Grid>
    );
  }

  // Multi select input which not displays more 2 elements in the field
  if (type === 'MonoSelect') {
    return (
      <Grid item xs={2}>
        <SelectFilter
          {...props}
          validate={props.required && [required()]}
          multiple={false}
        />
      </Grid>
    );
  }

  // Boolean input
  if (type === 'checkbox') {
    return (
      <Grid item>
        <CheckBoxFilter {...props} validate={props.required && [required()]} />
      </Grid>
    );
  }

  // Date range input
  if (type === 'DateRange') {
    return (
      <Grid
        item
        xs={4}
        container
        classes={{ container: classes.containerDateRange }}
        wrap="nowrap"
      >
        <DateRangeFilter {...props} defaultValue="" />
      </Grid>
    );
  }

  // Multi-Select with Radio button
  if (type === 'MultiRadio') {
    return (
      <Grid item xs={2}>
        <SelectFilter
          {...props}
          validate={props.required && [required()]}
          radio={true}
        />
      </Grid>
    );
  }

  const inputProps = {
    label: props.label,
    name: props.name,
    source: source,
    required: props.required,
    variant: props.variant,
    defaultValue: props.defaultValue,
    options: { autoComplete: 'off' }
  };
  // Default input, text input
  return (
    <Grid item xs={2}>
      <TextInput {...inputProps} />
    </Grid>
  );
};

Filter.defaultProps = {
  variant: 'standard',
  sort: true
};

const ReportFilters = props => {
  const { filters } = props;

  const filterParameters = useMemo(() => {
    return filters.map(item => item.parameter);
  }, [filters]);

  const crossFilters = useMemo(() => {
    return filterParameters.filter(
      filter =>
        filter.includes('fundFamiliesId') ||
        filter.includes('fundId') ||
        filter.includes('investorId') ||
        filter.includes('userId')
    );
  }, [filterParameters]);

  // Check if the report have some filters
  const hasFundFilter = useMemo(
    () => crossFilters.includes('fundIds') || crossFilters.includes('fundId'),
    [crossFilters]
  );
  const hasFundFamilyFilter = useMemo(
    () =>
      crossFilters.includes('fundFamiliesIds') ||
      crossFilters.includes('fundFamiliesId'),
    [crossFilters]
  );
  const hasInvestorFilter = useMemo(
    () =>
      crossFilters.includes('investorIds') ||
      crossFilters.includes('investorId'),
    [crossFilters]
  );
  const hasUserFilter = useMemo(
    () => crossFilters.includes('userIds') || crossFilters.includes('userId'),
    [crossFilters]
  );

  // Help to know when the filters are dependants with others, and render specific component
  // And to add another dependency, add a new match between two filters
  const filtersAreDependants = useMemo(() => {
    return (
      (hasFundFamilyFilter && hasInvestorFilter) ||
      (hasFundFilter && hasFundFamilyFilter) ||
      (hasFundFamilyFilter && hasUserFilter) ||
      (hasFundFilter && hasInvestorFilter) ||
      (hasUserFilter && hasInvestorFilter) ||
      (hasUserFilter && hasFundFilter) ||
      (hasFundFamilyFilter && hasFundFilter && hasInvestorFilter) ||
      (hasFundFamilyFilter &&
        hasFundFilter &&
        hasInvestorFilter &&
        hasUserFilter)
    );
  }, [hasFundFamilyFilter, hasFundFilter, hasInvestorFilter, hasUserFilter]);

  // Return nothing when no filters
  if (filters.length === 0) {
    return null;
  }

  return (
    <Grid container spacing={2} item xs={12} lg={9}>
      {filters.map((filter, index) => {
        const { parameter, name, ...rest } = filter;
        return (
          <Filter
            {...rest}
            name={`filters.${parameter}`}
            source={`filters.${parameter}`}
            key={index}
            filtersAreDependants={filtersAreDependants}
            filters={filters}
            crossFilters={crossFilters}
            sort={parameter !== 'categoryIds'}
          />
        );
      })}
    </Grid>
  );
};

ReportFilters.defaultProps = {
  filters: []
};

export default ReportFilters;
